import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import ReadAloud from "@/components/ReadAloud";
import FamilyNote from "@/components/FamilyNote";
import api, { formatError } from "@/lib/api";
import { serviceMeta, Badge, Stars } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Calendar, AlertTriangle, CheckCircle2, MessageCircle, CreditCard, X, Repeat, Footprints, DoorOpen, ClipboardCheck } from "lucide-react";
import VisitStatus from "@/components/VisitStatus";
import VisitWrapUp, { VisitSummaryCard } from "@/components/VisitWrapUp";
import FreeHelpers from "@/components/FreeHelpers";
import ThankYouComposer, { ThankYouCard } from "@/components/ThankYou";
import Avatar from "@/components/Avatar";
import SafetyCheckIn from "@/components/SafetyCheckIn";

export default function RequestDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [paying, setPaying] = useState(false);
  const [review, setReview] = useState({ rating: 5, comment: "" });
  const [rebooking, setRebooking] = useState(false);
  const [wrapUp, setWrapUp] = useState(false);

  const rebook = async () => {
    setRebooking(true);
    try {
      const { data } = await api.post(`/requests/${id}/rebook`);
      toast.success(`Booked again for ${data.scheduled_date}. ${req.helper_name} has been invited.`);
      navigate(`/requests/${data.id}`);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setRebooking(false); }
  };

  const load = useCallback(() => api.get(`/requests/${id}`).then((r) => setReq(r.data)), [id]);
  useEffect(() => { load(); }, [load]);

  if (!req) return <div className="max-w-3xl mx-auto px-4 py-16 text-lg">Loading…</div>;
  const meta = serviceMeta(req.service_type);
  const isSeeker = user && user.id === req.seeker_id;
  const isHelper = user && user.id === req.helper_id;

  const accept = async () => {
    try {
      await api.post(`/requests/${id}/accept`);
      toast.success("You've offered to help! You can now message the seeker.");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const setStatus = async (status) => {
    try {
      await api.post(`/requests/${id}/status`, { status });
      toast.success(status === "completed" ? "Visit completed" : "Status updated");
      await load();
      if (status === "completed" && isHelper) setWrapUp(true);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/simulate", { request_id: id });
      setReceipt(data);
      toast.success("Payment successful!");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setPaying(false); }
  };

  const submitReview = async () => {
    try {
      await api.post("/reviews", { request_id: id, helper_id: req.helper_id, rating: review.rating, comment: review.comment });
      toast.success("Thank you for your review!");
      setReview({ rating: 5, comment: "" });
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <div className="bg-white border border-[#E2D9CE] rounded-2xl p-6 sm:p-8 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <span className="w-14 h-14 rounded-2xl bg-[#FBEBE4] flex items-center justify-center">
            <meta.icon className="w-7 h-7 text-[#D96B43]" />
          </span>
          <div className="flex gap-2 flex-wrap justify-end">
            {req.urgency === "urgent" && <Badge color="red"><AlertTriangle className="w-4 h-4" /> Urgent</Badge>}
            <Badge color={req.payment_type === "paid" ? "orange" : "teal"}>
              {req.payment_type === "paid" ? `$${req.amount} Paid` : "Voluntary"}
            </Badge>
            <Badge color="slate" testid="request-status">{req.status.replace("_", " ")}</Badge>
          </div>
        </div>
        <h1 className="text-3xl font-display font-bold text-slate-900">{req.title}</h1>
        <p className="text-slate-500 mt-1">{meta.label} · posted by {req.seeker_name}</p>
        <div className="mt-3"><ReadAloud size="lg" testId="request-read-aloud" label="Read this page aloud"
          text={`${req.title}. ${req.description} At ${req.location}, on ${(req.scheduled_at || req.scheduled_date || "").replace("T", " at ")}. ${req.payment_type === "paid" ? `Paid, ${req.amount}.` : "Voluntary."} Status: ${req.status.replace("_", " ")}.${req.helper_name ? ` Your helper is ${req.helper_name}.` : ""}`} /></div>
        <p className="text-lg text-slate-700 leading-relaxed mt-5">{req.description}</p>
        <FamilyNote note={req.family_note} canReply={isHelper} requestId={req.id} onReplied={(reply) => setReq({ ...req, family_note: { ...req.family_note, reply } })} />
        <div className="mt-5 space-y-2 text-slate-700">
          <p className="flex items-center gap-2"><MapPin className="w-5 h-5 text-[#2E7D6B]" /> {req.location}</p>
          <p className="flex items-center gap-2"><Calendar className="w-5 h-5 text-[#2E7D6B]" /> {req.scheduled_date}</p>
          {req.helper_name && (
            <p className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#16A34A]" /> Helper:
              <Link to={`/helpers/${req.helper_id}`} className="flex items-center gap-2 font-semibold hover:text-[#D96B43]">
                <Avatar user={{ name: req.helper_name, avatar: req.helper_avatar }} size="sm" className="!w-9 !h-9 !text-base" /> {req.helper_name}
              </Link>
            </p>
          )}
          {req.preferred_helper_name && req.status === "open" && (
            <p data-testid="repeat-note" className="flex items-center gap-2 text-[#2E7D6B]"><Repeat className="w-5 h-5" /> Repeat booking — {req.preferred_helper_name} has been invited</p>
          )}
          <VisitStatus request={req} />
        </div>

        {/* Actions */}
        <div className="mt-8 flex flex-wrap gap-3">
          {!user && (
            <Link to="/login" className="bg-[#D96B43] hover:bg-[#C25832] text-white font-semibold px-6 min-h-[52px] rounded-xl flex items-center transition-colors">
              Log in to help
            </Link>
          )}
          {user?.role === "helper" && req.status === "open" && (
            <button data-testid="accept-request-btn" onClick={accept}
              className="bg-[#2E7D6B] hover:bg-[#256658] text-white font-semibold px-6 min-h-[52px] rounded-xl transition-colors">
              Offer to help
            </button>
          )}
          {(isSeeker || isHelper) && req.helper_id && (
            <button data-testid="open-messages-btn" onClick={() => navigate("/messages")}
              className="border-2 border-[#E2D9CE] hover:border-[#D96B43] text-slate-800 font-semibold px-6 min-h-[52px] rounded-xl flex items-center gap-2 transition-colors">
              <MessageCircle className="w-5 h-5" /> Message
            </button>
          )}
          {isSeeker && req.payment_type === "paid" && !req.paid && req.helper_id && (
            <button data-testid="pay-btn" onClick={() => setShowPay(true)}
              className="bg-[#D96B43] hover:bg-[#C25832] text-white font-semibold px-6 min-h-[52px] rounded-xl flex items-center gap-2 transition-colors">
              <CreditCard className="w-5 h-5" /> Pay ${req.amount}
            </button>
          )}
          {req.paid && <Badge color="green" testid="paid-badge"><CheckCircle2 className="w-4 h-4" /> Paid</Badge>}
          {(isSeeker || isHelper) && req.status === "matched" && (
            <button data-testid="start-btn" onClick={() => setStatus("in_progress")}
              className="bg-[#2E7D6B] hover:bg-[#256658] text-white font-semibold px-6 min-h-[52px] rounded-xl flex items-center gap-2 transition-colors">
              <Footprints className="w-5 h-5" /> {isHelper ? "I've arrived" : "Helper has arrived"}
            </button>
          )}
          {(isSeeker || isHelper) && req.status === "in_progress" && (
            <button data-testid="complete-btn" onClick={() => setStatus("completed")}
              className="bg-[#16A34A] hover:bg-green-700 text-white font-semibold px-6 min-h-[52px] rounded-xl flex items-center gap-2 transition-colors">
              <DoorOpen className="w-5 h-5" /> {isHelper ? "I've left — visit done" : "Visit completed"}
            </button>
          )}
          {isSeeker && req.status === "completed" && req.helper_id && (
            <button data-testid="rebook-btn" onClick={rebook} disabled={rebooking}
              className="bg-[#2E7D6B] hover:bg-[#256658] disabled:opacity-60 text-white font-semibold px-6 min-h-[52px] rounded-xl flex items-center gap-2 transition-colors">
              <Repeat className="w-5 h-5" /> {rebooking ? "Booking…" : "Book again next week"}
            </button>
          )}
          {isSeeker && req.status !== "completed" && req.status !== "open" && (
            <button data-testid="cancel-btn" onClick={() => setStatus("cancelled")}
              className="border-2 border-red-200 hover:border-red-400 text-red-600 font-semibold px-6 min-h-[52px] rounded-xl transition-colors">
              Cancel match
            </button>
          )}
        </div>

        {/* Free helpers at the chosen slot */}
        {isSeeker && req.status === "open" && req.scheduled_at && <FreeHelpers requestId={id} scheduledAt={req.scheduled_at} />}

        {/* Visit summary */}
        {req.status === "completed" && (req.visit_summary ? <VisitSummaryCard request={req} isSeeker={isSeeker} onChanged={load} /> : isHelper && !wrapUp && (
          <div className="mt-8 pt-6 border-t border-[#E2D9CE]">
            <button data-testid="add-summary-btn" onClick={() => setWrapUp(true)} className="flex items-center gap-2 bg-[#E8F4F1] hover:bg-[#d6ebe5] text-[#1F5F4F] font-semibold px-5 min-h-[52px] rounded-xl">
              <ClipboardCheck className="w-5 h-5" /> Add a visit summary for the family
            </button>
          </div>
        ))}
        {wrapUp && isHelper && !req.visit_summary && (
          <VisitWrapUp asModal requestId={id} onDone={() => { setWrapUp(false); load(); }} onSkip={() => setWrapUp(false)} />
        )}

        {/* Safety check-in */}
        {isSeeker && req.status === "completed" && req.safety_status && (
          <div className="mt-8"><SafetyCheckIn request={req} onChanged={load} /></div>
        )}

        {/* Thank-you card */}
        {req.status === "completed" && req.helper_id && (
          req.thank_you ? (
            <div className="mt-8 pt-6 border-t border-[#E2D9CE]">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Thank-you card</h3>
              <ThankYouCard note={req.thank_you} />
            </div>
          ) : isSeeker && <ThankYouComposer requestId={id} helperName={req.helper_name} onSent={load} />
        )}

        {/* Review */}
        {isSeeker && req.status === "completed" && req.helper_id && (
          <div className="mt-8 pt-6 border-t border-[#E2D9CE]">
            <h3 className="text-xl font-semibold text-slate-900">Leave a review for {req.helper_name}</h3>
            <div className="flex gap-1 my-3" data-testid="review-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setReview({ ...review, rating: n })}
                  className={`text-3xl ${n <= review.rating ? "text-[#D99B26]" : "text-slate-300"}`}>★</button>
              ))}
            </div>
            <textarea data-testid="review-comment" rows={3} value={review.comment} onChange={(e) => setReview({ ...review, comment: e.target.value })}
              placeholder="How was your experience?" className="w-full px-4 py-3 rounded-xl border border-[#E2D9CE] focus:border-[#D96B43] outline-none" />
            <button data-testid="submit-review-btn" onClick={submitReview}
              className="mt-3 bg-[#D96B43] hover:bg-[#C25832] text-white font-semibold px-6 min-h-[48px] rounded-xl transition-colors">
              Submit review
            </button>
          </div>
        )}
      </div>

      {/* Payment modal */}
      {showPay && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !receipt && setShowPay(false)}>
          <div className="bg-white rounded-2xl p-6 sm:p-8 w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="payment-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-display font-bold text-slate-900">{receipt ? "Payment receipt" : "Checkout"}</h3>
              <button onClick={() => setShowPay(false)} className="p-1"><X className="w-6 h-6" /></button>
            </div>
            {receipt ? (
              <div className="text-center py-4" data-testid="payment-receipt">
                <CheckCircle2 className="w-16 h-16 text-[#16A34A] mx-auto" />
                <p className="text-lg font-semibold text-slate-900 mt-3">Paid ${receipt.amount}</p>
                <p className="text-slate-500">Receipt #{receipt.receipt_id}</p>
                <p className="text-sm text-slate-400 mt-2">Simulated payment — no real money charged.</p>
                <button onClick={() => setShowPay(false)} className="mt-5 w-full bg-[#D96B43] text-white font-semibold min-h-[48px] rounded-xl">Done</button>
              </div>
            ) : (
              <>
                <p className="text-slate-600 mb-4">You're paying <span className="font-semibold text-slate-900">${req.amount}</span> for “{req.title}”.</p>
                <div className="bg-[#FFF7E6] text-[#A9741A] text-sm rounded-xl p-3 mb-4">This is a simulated payment for the demo. No card is charged.</div>
                <button data-testid="confirm-payment-btn" onClick={pay} disabled={paying}
                  className="w-full bg-[#D96B43] hover:bg-[#C25832] disabled:opacity-60 text-white font-semibold min-h-[52px] rounded-xl transition-colors">
                  {paying ? "Processing…" : `Pay $${req.amount}`}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
